/**
 * One turn's work, folded into one row.
 *
 * A turn emits a step per tool call, so a two-tool answer would otherwise stack
 * five blocks — reason, tool, tool, reason, answer — each its own card, each
 * pushing the answer further down the page. The reader would have to scroll
 * past the machine's working to reach its conclusion, and while it streams the
 * page grows under them.
 *
 * So: the steps collapse into a single header that says what is happening now
 * and how many steps it took, and the answer renders below it at full size.
 * Expanding shows the same steps on a rail.
 *
 * Duration is measured HERE rather than carried on the items: elapsed time is a
 * property of watching, not of the turn, and a replayed thread must not claim
 * to have taken however long the replay took.
 */
import {
  Brain,
  Check,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileText,
  Layers,
  Loader2,
  Search,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Markdown } from '@/components/Markdown'
import type { ToolItem } from '@/data/chat'
import { cn } from '@/lib/utils'

/**
 * One entry in the folded work.
 *
 * Reasoning is carried as text rather than as its message, because the split is
 * per-FIELD, not per-item: the assistant message of a turn has both a reasoning
 * summary (work) and the answer (not work), and they belong on opposite sides
 * of the fold.
 */
export type Step =
  | { id: string; kind: 'tool'; item: ToolItem }
  | { id: string; kind: 'reasoning'; text: string }

const TOOL_ICONS: Record<ToolItem['tool'], LucideIcon> = {
  search_meetings: Search,
  read_notes: FileText,
  list_spaces: Layers,
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

export function AgentSteps({ steps, running }: { steps: Step[]; running: boolean }) {
  const [expanded, setExpanded] = useState(false)

  // The clock only advances while the turn is live; once it ends the label
  // freezes at the elapsed total rather than counting on forever.
  const [startedAt] = useState(() => Date.now())
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])
  useEffect(() => {
    if (!running) setNow(Date.now())
  }, [running])

  const elapsed = Math.max(0, Math.round((now - startedAt) / 1000))

  const currentTool = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      const step = steps[i]!
      if (step.kind === 'tool') return step.item
    }
    return null
  }, [steps])

  if (steps.length === 0) return null

  const label = running ? 'Working' : elapsed > 0 ? `Worked for ${formatDuration(elapsed)}` : 'Worked'
  const subtitle = [currentTool?.title ?? 'Agent', running ? formatDuration(elapsed) : null]
    .filter(Boolean)
    .join(' · ')
  const ToggleIcon = expanded ? ChevronsDownUp : ChevronsUpDown

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)]/50">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="relative flex w-full items-center gap-2.5 px-3 py-2 pl-4 text-left"
        aria-expanded={expanded}
        aria-label={`${label}, ${steps.length} steps`}
      >
        <span
          className={cn(
            'absolute top-2 bottom-2 left-0 w-0.5 rounded-r-full',
            running ? 'agent-steps-pulse bg-[var(--color-primary)]' : 'bg-[var(--color-border-strong)]'
          )}
          aria-hidden
        />
        <Brain
          className={cn(
            'size-3.5 shrink-0',
            running ? 'text-[var(--color-primary)]' : 'text-[var(--color-fg-subtle)]'
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-[var(--color-fg)]">{label}</span>
          <span className="mt-0.5 block truncate text-2xs text-[var(--color-fg-subtle)]">
            {subtitle}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-2xs font-medium text-[var(--color-fg-subtle)]">
          {steps.length} step{steps.length === 1 ? '' : 's'}
          <ToggleIcon className="size-3" />
        </span>
      </button>

      {expanded ? (
        <div className="space-y-2 border-t border-[var(--color-border-subtle)] px-3 py-2.5">
          {steps.map((step) =>
            step.kind === 'tool' ? (
              <ToolCard key={step.id} item={step.item} />
            ) : (
              <Reasoning key={step.id} text={step.text} />
            )
          )}
        </div>
      ) : null}
    </div>
  )
}

function Reasoning({ text }: { text: string }) {
  return (
    <div className="border-l-2 border-[var(--color-border-strong)] pl-3 text-xs leading-relaxed text-[var(--color-fg-subtle)] italic">
      <Markdown>{text}</Markdown>
    </div>
  )
}

function ToolCard({ item }: { item: ToolItem }) {
  const [open, setOpen] = useState(false)
  const Icon = TOOL_ICONS[item.tool] ?? Wrench
  const running = item.outcome === null

  return (
    <div className="rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-surface)]/60">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
        aria-expanded={open}
      >
        {running ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-[var(--color-primary)]" />
        ) : item.outcome === 'ok' ? (
          <Check className="size-3.5 shrink-0 text-[var(--color-success)]" />
        ) : (
          <X className="size-3.5 shrink-0 text-[var(--color-danger)]" />
        )}
        <Icon className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-muted)]">
          {item.title}
          {item.summary ? (
            <span className="ml-1.5 text-[var(--color-fg-subtle)]">— {item.summary}</span>
          ) : null}
        </span>
        <ChevronRight
          className={cn(
            'size-3 shrink-0 text-[var(--color-fg-subtle)] transition-transform',
            open && 'rotate-90'
          )}
        />
      </button>

      {open ? (
        <div className="border-t border-[var(--color-border-subtle)] px-2.5 py-2">
          <pre className="overflow-x-auto rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)] p-2 font-mono text-2xs text-[var(--color-fg-muted)]">
            {JSON.stringify(item.argsPreview, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  )
}
